/**
 * verse-result.ts: the curated-verse-match lane's two read paths (rule
 * #80, share-scripture-found arc).
 *
 *   - getVersesForPrayer — the saved -> session-cache -> fetch -> persist
 *     chain. Consumers: the share flows' Find-Scripture-for-this-prayer
 *     path (ShareVersePicker's found view) AND the Garden vine card's
 *     "Find" verse action (Chunk 2). These are the verse SEARCH surfaces;
 *     a fresh match persists `Prayer.verseResult`.
 *   - resolveSavedVerses (below) — a SAVED-ONLY, READ-ONLY resolve.
 *     Consumers: PrayerDetail's read-only "Scripture for this prayer"
 *     view AND the Garden vine card's "View" verse action. It NEVER
 *     searches and NEVER writes — it only re-resolves an already-saved
 *     verseResult from the local bundle.
 *
 * Contract (the verse-suggest.ts rule #59 split, extended):
 *   - returns a non-empty SuggestedVerse[] on a match,
 *   - returns null for honest absence (crisis / silence / no-fit /
 *     empty text) so callers render silence, never an error,
 *   - THROWS VerseSuggestError on rate-limit / network / server so
 *     callers can render the gentle error + retry states.
 *
 * Persistence (migration 032): a successful FRESH match writes
 * `Prayer.verseResult` ({ ref, category } pairs + matchedAt, ids only,
 * never prose) via the store's serialized `patchPrayer`, guarded on
 * rawText so a mid-flight raw-text edit (which clears verseResult in its
 * atomic payload) can never be clobbered by a stale write. Silence NEVER
 * persists (the fit judge has run-to-run variance;
 * a transient no-fit must not permanently silence the lane).
 *
 * Saved path: entries re-resolve against the struggle-verses bundle at
 * render (zero network, zero rate spend). Partial bundle drift self-heals
 * by re-persisting the survivors PRESERVING matchedAt (a cleanup, not a
 * new match). Total drift or a bundle load failure falls through to the
 * wire fetch UNDER THE CALLER'S EXPLICIT TAP: the response carries
 * everything needed to render, so a broken bundle never dead-ends the
 * lane and the user is never shown false silence over a result that
 * exists. That fallback is the ONE recorded exception to the zero-spend
 * saved-render claim (plan section 4 step 2 carries the bounds).
 *
 * OWNER BINDING (rule #90): the final wire boundary rechecks the exact
 * durable owner, revision, and raw text after authentication. No prayer
 * text crosses the wire from the SAVED-ONLY read (resolveSavedVerses). The
 * crisis mirror in both helpers is defense in depth.
 */

import type { Prayer, VerseResultEntry } from '@/types/prayer';
import { isCrisisPrayer } from '@/lib/spine-crisis';
import {
  suggestVersesCached,
  type SuggestedVerse,
  type VerseStruggleCategory,
} from '@/lib/verse-suggest';
import {
  loadStruggleVerses,
  findBundleEntry,
} from '@/lib/bible-search/struggle-verses';
import { useAdiaStore } from '@/lib/store';
import { isCoreReadOnly } from '@/lib/core-cutover';
import { prayerOutboundExpectation } from '@/lib/outbound-prayer-authorization';

export async function getVersesForPrayer(
  prayer: Prayer,
): Promise<SuggestedVerse[] | null> {
  const rawText = prayer.rawText;
  if (!rawText.trim()) return null;
  // Crisis re-gate at render (plan decision 4): a crisis pattern adopted
  // AFTER a result was saved suppresses the saved render retroactively.
  // The stored row is NOT cleared (patterns can loosen; the data is
  // inert while suppressed).
  if (isCrisisPrayer(rawText)) return null;

  const saved = prayer.verseResult;
  if (saved && saved.entries.length > 0) {
    try {
      const bundle = await loadStruggleVerses();
      const resolved: SuggestedVerse[] = [];
      const survivors: VerseResultEntry[] = [];
      for (const e of saved.entries) {
        const entry = findBundleEntry(bundle, e.category, e.ref);
        if (entry) {
          // The lookup succeeding against the bundle is what validates
          // the persisted string back into the closed category union
          // (the type is deliberately `string` at the persistence
          // boundary; see VerseResultEntry).
          resolved.push({ ...entry, category: e.category as VerseStruggleCategory });
          survivors.push(e);
        }
      }
      if (resolved.length > 0) {
        if (!isCoreReadOnly() && survivors.length !== saved.entries.length) {
          // Partial drift self-heal: re-persist the survivors so the
          // saved row matches what renders. PRESERVES matchedAt (a
          // cleanup, not a new match). Fire-and-forget; a write failure
          // leaves this render intact and the next open retries.
          void useAdiaStore
            .getState()
            .patchPrayer(
              prayer.id,
              { verseResult: { entries: survivors, matchedAt: saved.matchedAt } },
              (latest) => latest.rawText === rawText,
            )
            .catch(() => {});
        }
        return resolved;
      }
      // Zero entries resolved (bundle regenerated past the saved refs):
      // fall through to the fetch under the caller's tap.
    } catch {
      // Bundle load failure (transient fetch reject or version skew):
      // fall through to the fetch (plan section 4 step 2).
    }
  }

  const res = await suggestVersesCached(
    { id: prayer.id, updatedAt: prayer.updatedAt, rawText },
    prayerOutboundExpectation(prayer),
  );
  if (res.silence !== null || res.verses.length === 0) return null;
  const entries: VerseResultEntry[] = res.verses.map((v) => ({
    ref: v.ref,
    category: v.category,
  }));
  if (!isCoreReadOnly()) {
    void useAdiaStore
      .getState()
      .patchPrayer(
        prayer.id,
        { verseResult: { entries, matchedAt: new Date().toISOString() } },
        (latest) => latest.rawText === rawText,
      )
      .catch(() => {});
  }
  return res.verses;
}

/**
 * resolveSavedVerses — the SAVED-ONLY, READ-ONLY resolve behind
 * PrayerDetail's "Scripture for this prayer" view (cut-tap-to-match plan,
 * section 5). DISTINCT from getVersesForPrayer above: it NEVER calls the
 * matching API and NEVER writes (no patchPrayer -> pushPrayer -> Supabase
 * upsert). Searching for scripture is a SHARE-moment OR Garden "Find"
 * action; the read-only surfaces (PrayerDetail's line, the Garden card's
 * "View") just READ what a search already saved.
 *
 * It reads prayer.verseResult, resolves each entry against the local
 * struggle-verses bundle, and returns the resolved SuggestedVerse[].
 * Returns [] (the caller hides its line, no dead affordance) on:
 *   - a crisis-matching rawText (rule #76 re-gate, mirrors
 *     getVersesForPrayer's line 62; the stored row is NOT cleared —
 *     patterns can loosen, and this pure-read path never writes anyway),
 *   - no saved result,
 *   - total drift (no saved entry resolves against the current bundle),
 *   - a bundle load failure.
 * Partial drift is NOT self-healed here (a re-persist would write); a
 * drifted entry is simply dropped from the render, and the share flow's
 * getVersesForPrayer re-persists the survivors on its next open. The
 * bundle fetch is a public static asset (rule #62/#76) — no prayer data
 * crosses the wire.
 */
export async function resolveSavedVerses(prayer: Prayer): Promise<SuggestedVerse[]> {
  const rawText = prayer.rawText;
  if (!rawText.trim()) return [];
  if (isCrisisPrayer(rawText)) return [];

  const saved = prayer.verseResult;
  if (!saved || saved.entries.length === 0) return [];

  const bundle = await loadStruggleVerses().catch(() => null);
  if (!bundle) return [];

  const resolved: SuggestedVerse[] = [];
  for (const e of saved.entries) {
    const entry = findBundleEntry(bundle, e.category, e.ref);
    if (entry) {
      // The lookup succeeding validates the persisted string back into
      // the closed category union (the type is `string` at the
      // persistence boundary; see VerseResultEntry).
      resolved.push({ ...entry, category: e.category as VerseStruggleCategory });
    }
  }
  return resolved;
}
